import { useCallback, useState } from 'react';
import type { PaymentMountConfig } from './types';

const readOpenFromUrl = (queryKey: string): boolean => {
  if (!queryKey) return false;
  return new URL(window.location.href).searchParams.get(queryKey) === '1';
};

const writeOpenToUrl = (queryKey: string, open: boolean): void => {
  if (!queryKey) return;

  const url = new URL(window.location.href);
  if (open) {
    url.searchParams.set(queryKey, '1');
  } else {
    url.searchParams.delete(queryKey);
  }

  const nextHref = `${url.pathname}${url.search}${url.hash}`;
  const currentHref = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (nextHref !== currentHref) {
    window.history.pushState({ ipsPaymentAdd: open }, '', nextHref);
  }
};

const hasEndpointInPath = (pathname: string, endpoint: string): boolean => {
  if (!endpoint) return false;
  const escaped = endpoint.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(?:^|/)${escaped}(?:/|$)`);
  return regex.test(pathname);
};

export const usePaymentOverlay = (config: PaymentMountConfig) => {
  const [open, setOpen] = useState<boolean>(() => config.openFromData || readOpenFromUrl(config.openQueryKey));
  const [loading, setLoading] = useState<boolean>(() => config.openFromData || readOpenFromUrl(config.openQueryKey));

  const openModal = useCallback(
    (syncUrl = true) => {
      setLoading(true);
      setOpen(true);
      if (syncUrl) {
        writeOpenToUrl(config.openQueryKey, true);
      }
    },
    [config.openQueryKey]
  );

  const closeModal = useCallback(
    (syncUrl = true) => {
      setOpen(false);
      if (syncUrl) {
        writeOpenToUrl(config.openQueryKey, false);
      }
    },
    [config.openQueryKey]
  );

  const syncFromHistory = useCallback(() => {
    if (readOpenFromUrl(config.openQueryKey)) {
      openModal(false);
    } else {
      closeModal(false);
    }
  }, [closeModal, config.openQueryKey, openModal]);

  const handleIframeLoad = useCallback(
    (iframe: HTMLIFrameElement | null) => {
      setLoading(false);

      try {
        const iframeWindow = iframe?.contentWindow;
        if (!iframeWindow) return;

        const path = iframeWindow.location.pathname || '';
        const landedOnList =
          hasEndpointInPath(path, config.listEndpoint) &&
          (config.addEndpoint ? !hasEndpointInPath(path, config.addEndpoint) : true);

        if (landedOnList) {
          writeOpenToUrl(config.openQueryKey, false);
          window.location.reload();
        }
      } catch (error) {
        // Ignore cross-origin or temporary access errors and keep modal open.
      }
    },
    [config.addEndpoint, config.listEndpoint, config.openQueryKey]
  );

  return {
    open,
    loading,
    openModal,
    closeModal,
    syncFromHistory,
    handleIframeLoad,
  };
};
